'use client'

import { usePlaygroundInfo } from '../playground/Playground'
import { Transition } from '@headlessui/react'
import { LinkIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {
  Alert,
  AlertActions,
  AlertBody,
  AlertDescription,
  AlertTitle,
  Button,
  Spinner,
  Text
} from '@criteria-labs/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { ProjectManager, isProjectFolder, useCurrentProjectItems, visitProjectItems } from '../project'

function useTimeout(callback: () => void, ms?: number | undefined): void {
  const savedCallback = useRef(callback)
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const timeout = setTimeout(() => savedCallback.current(), ms)
    return () => clearTimeout(timeout)
  }, [ms])
}

function CopiedNotification({ remove }: { remove: () => void }): JSX.Element {
  const [show, setShow] = useState(false)
  useEffect(() => {
    setShow(true)
  }, [])

  useTimeout(() => {
    setShow(false)
  }, 3000)
  useTimeout(() => {
    remove()
  }, 3300)

  return (
    <Transition
      show={show}
      enter="transform ease-out duration-200 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-500"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Text>Copied!</Text>
    </Transition>
  )
}

export function SharePanel({ view, className }: { view: 'editor' | 'docs'; className?: string }) {
  const { snapshot, snapshotEdited, createSnapshot, snapshotHref } = usePlaygroundInfo()

  const [isCreatingSnapshot, setCreatingSnapshot] = useState(false)
  const [copied, setCopied] = useState(false)

  const currentProjectItems = useCurrentProjectItems()
  let filesCount = 0
  const userFiles: string[] = []
  visitProjectItems(currentProjectItems, (item, parentFolder) => {
    if (!isProjectFolder(item)) {
      filesCount += 1
    }
    if (item.store === 'userFileSystem' && parentFolder === null) {
      userFiles.push(item.name)
    }
    return false
  })

  const [showShareFilesAlert, setShowShareFilesAlert] = useState(false)

  const handleClick = async () => {
    if (userFiles.length > 0) {
      setShowShareFilesAlert(true)
    } else {
      try {
        setCreatingSnapshot(true)
        await createSnapshot(view)
      } catch (error) {
        console.log(error)
      } finally {
        setCreatingSnapshot(false)
      }
    }
  }

  return (
    <>
      <div className={clsx(className, 'flex items-center gap-3')}>
        <Button
          onClick={handleClick}
          color="indigo"
          disabled={filesCount === 0 || isCreatingSnapshot}
          className="min-w-20"
        >
          {!isCreatingSnapshot && <>Share</>}
          {isCreatingSnapshot && <Spinner data-slot="icon" />}
        </Button>
        {snapshot && snapshotHref && !snapshotEdited && (
          <Button
            plain
            onClick={async () => {
              await navigator.clipboard.writeText(snapshotHref)
              setCopied(true)
            }}
          >
            <LinkIcon />
            <span className="hidden md:inline-block">
              {'.../'}
              {snapshot.id}
              {snapshot.view === 'docs' && '?view=docs'}
            </span>
          </Button>
        )}

        {copied && (
          <CopiedNotification
            remove={() => {
              setCopied(false)
            }}
          />
        )}
      </div>
      <Alert open={showShareFilesAlert} onClose={setShowShareFilesAlert}>
        <AlertTitle>Share files?</AlertTitle>
        <AlertDescription>
          The following files and folders on your device will be uploaded and made available to the public:
        </AlertDescription>
        <AlertBody>
          <ul>
            {userFiles.map((file) => (
              <li key={file}>
                <Text>{file}</Text>
              </li>
            ))}
          </ul>
        </AlertBody>
        <AlertActions>
          <Button plain onClick={() => setShowShareFilesAlert(false)}>
            Cancel
          </Button>
          <Button
            color="indigo"
            onClick={async () => {
              setShowShareFilesAlert(false)
              try {
                setCreatingSnapshot(true)
                await createSnapshot(view)
              } catch (error) {
                console.log(error)
              } finally {
                setCreatingSnapshot(false)
              }
            }}
          >
            Share
          </Button>
        </AlertActions>
      </Alert>
    </>
  )
}
